import React, { useState, useEffect } from 'react';
import { FileText, ChevronDown, Download, User, Calendar } from 'lucide-react';
import { establishmentAPI, employeeAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';

const Reports = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State management
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedForm, setSelectedForm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  // Available forms
  const forms = [
    { value: 'form_5', label: 'Form V - Muster Roll' },
    { value: 'form_X', label: 'Form X - Employment Card' },
    { value: 'form_XIII', label: 'Form XIII - Overtime Register' },
    { value: 'form_XXIII', label: 'Form XXIII - Wages Register' },
    { value: 'form_XXIX', label: 'Form XXIX - Wage Slip' },
    { value: 'form_a', label: 'Form A - Annual Return' },
    { value: 'form_banking', label: 'Banking Details' },
    { value: 'form_ecr', label: 'ECR - Electronic Challan cum Return' },
    { value: 'form_empcard', label: 'Employee Card' },
    { value: 'form_q', label: 'Form Q - Register of Wages' },
    { value: 'form_s', label: 'Form S - Register of Overtime' },
    { value: 'form_t', label: 'Form T - Register of Deductions' },
    { value: 'form19payslip', label: 'Form 19 - Payslip' }
  ];

  // Month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Year options
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch establishments on mount (SuperAdmin only)
  useEffect(() => {
    if (lockedEstId) { setLoading(false); return; }
    const fetchEstablishments = async () => {
      try {
        setLoading(true);
        const response = await establishmentAPI.getAll();
        setEstablishments(response.establishments || []);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        alert('Error loading organizations: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEstablishments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch employees when establishment changes
  const handleEstablishmentChange = async (e) => {
    const value = e.target.value;
    setSelectedEstablishment(value);
    setSelectedEmployee('all');
    setEmployees([]);
    setFormData(null);

    if (value) {
      try {
        setLoading(true);
        const response = await employeeAPI.getByEstablishment(value);
        setEmployees(response.employees || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
        alert('Error loading employees: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGenerateForm = async () => {
    if (!selectedEstablishment) {
      alert('Please select an organization');
      return;
    }

    if (!selectedForm) {
      alert('Please select a form type');
      return;
    }

    setIsGenerating(true);

    try {
      // Call API to generate form
      const params = new URLSearchParams({
        establishment_id: selectedEstablishment,
        month: selectedMonth,
        year: selectedYear
      });

      if (selectedEmployee !== 'all') {
        params.append('employee_id', selectedEmployee);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/reports/${selectedForm}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate form');
      }

      const data = await response.json();
      setFormData(data);

      console.log('Form generated:', data);
    } catch (error) {
      console.error('Error generating form:', error);
      alert('Error generating form: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const getMonthLabel = () => {
    const month = months.find(m => m.value === parseInt(selectedMonth));
    return month ? `${month.label} ${selectedYear}` : `${selectedMonth}/${selectedYear}`;
  };

  return (
    <div className="new-employee-container">
      <div className="new-employee-header-section">
        <div>
          <h2 className="new-employee-header">
            <FileText size={28} style={{ marginRight: '12px' }} />
            Generate Forms & Reports
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', marginTop: '8px' }}>
            Select parameters and generate statutory forms and reports
          </p>
        </div>
      </div>

      {/* Establishment Selector */}
      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstablishment}
          onChange={handleEstablishmentChange}
          loading={loading}
        />
      )}

      <div className="new-employee-form">
        {/* Filters Section */}
        <div className="new-employee-content">
          <h3 className="new-employee-section-header">Form Parameters</h3>
          <p className="new-employee-section-description">
            Select organization, employee, form type, and period
          </p>

          <div className="new-employee-field-group">

            {/* Employee */}
            <div>
              <label className="new-employee-field-label">
                <User size={16} style={{ marginRight: '6px' }} />
                Employee
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="new-employee-input"
                  disabled={!selectedEstablishment || loading}
                  style={{ appearance: 'none', paddingRight: '2.5rem' }}
                >
                  <option value="all">All Employees</option>
                  {employees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_id})
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6b7280'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="new-employee-field-group">
            {/* Form Type */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="new-employee-field-label">
                <FileText size={16} style={{ marginRight: '6px' }} />
                Form Type <span className="new-employee-required">*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value)}
                  className="new-employee-input"
                  style={{ appearance: 'none', paddingRight: '2.5rem' }}
                >
                  <option value="">Select Form Type</option>
                  {forms.map(form => (
                    <option key={form.value} value={form.value}>
                      {form.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6b7280'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="new-employee-field-group">
            {/* Month */}
            <div>
              <label className="new-employee-field-label">
                <Calendar size={16} style={{ marginRight: '6px' }} />
                Month <span className="new-employee-required">*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="new-employee-input"
                  style={{ appearance: 'none', paddingRight: '2.5rem' }}
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6b7280'
                  }}
                />
              </div>
            </div>

            {/* Year */}
            <div>
              <label className="new-employee-field-label">
                <Calendar size={16} style={{ marginRight: '6px' }} />
                Year <span className="new-employee-required">*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="new-employee-input"
                  style={{ appearance: 'none', paddingRight: '2.5rem' }}
                >
                  {years.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6b7280'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div style={{ marginTop: '2rem' }}>
            <button
              onClick={handleGenerateForm}
              disabled={isGenerating || !selectedEstablishment || !selectedForm}
              className="new-employee-btn-create"
              style={{ width: '100%' }}
            >
              {isGenerating ? (
                <>
                  <div className="new-employee-spinner"></div>
                  Generating...
                </>
              ) : (
                <>
                  <FileText size={16} />
                  Generate Form - {getMonthLabel()}
                </>
              )}
            </button>
          </div>

          {/* Form Preview/Data Section */}
          {formData && (
            <div style={{ marginTop: '2rem' }}>
              <div style={{
                padding: '1.5rem',
                background: '#f0fdf4',
                border: '1.5px solid #10b981',
                borderRadius: '0.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ color: '#059669', fontWeight: '600', margin: 0 }}>
                    Form Generated Successfully
                  </h4>
                  <button
                    className="new-employee-btn-next"
                    style={{ minWidth: 'auto', padding: '0.5rem 1rem' }}
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
                <p style={{ color: '#065f46', fontSize: '0.875rem', margin: 0 }}>
                  {forms.find(f => f.value === selectedForm)?.label} - {getMonthLabel()}
                </p>
                {selectedEmployee !== 'all' && (
                  <p style={{ color: '#065f46', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                    Employee: {employees.find(e => e.employee_id === selectedEmployee)?.first_name} {employees.find(e => e.employee_id === selectedEmployee)?.last_name}
                  </p>
                )}
              </div>

              {/* Form data preview (you can customize this based on form type) */}
              <div style={{ marginTop: '1rem' }}>
                <pre style={{
                  background: 'var(--theme-surfaceHover)',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  maxHeight: '400px'
                }}>
                  {JSON.stringify(formData, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
